import { Text as TextWrapper } from '@oneaudi/unified-web-components';
import React, { memo, useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import type { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import { renderTextWithFootnotesReferencesV2 } from '@oneaudi/feature-app-utils';
import { UeElement } from '@oneaudi/falcon-tools';
import { convertRichText } from '../utils/textUtils';
import { useTrackClick } from '../context/TrackingContext';

const StyledText = styled.span`
  & p {
    margin: 0;
    padding: 0;
    margin-block-start: var(--spacing-relative-xs);
  }

  & a {
    color: inherit;
  }
`;

export interface TextProps {
  readonly text: string;
  trackingService?: TrackingServiceV2;
}

export const RichText = memo(function RichText({ text, trackingService }: TextProps) {
  const richText = convertRichText(text);

  const trackClick = useTrackClick();
  const textRef = useRef<HTMLElement | null>(null);

  const handleClick = useCallback((anchor: HTMLAnchorElement, index: number) => {
    trackClick?.({
      targetURL: anchor.getAttribute('href') || anchor.href,
      label: anchor.innerHTML,
      pos: index + 1,
      action: trackingService?.evaluateLinkType(anchor.href),
      elementName: 'text link',
    });
  }, []);

  useEffect(() => {
    const container = textRef.current;
    const anchorList: NodeListOf<HTMLAnchorElement> | undefined = container?.querySelectorAll('a');

    if (!anchorList) return;

    anchorList.forEach((anchor, index) => {
      const clickHandler: () => void = () => {
        handleClick(anchor, index);
      };
      anchor.addEventListener('click', clickHandler);
      return () => {
        anchor.removeEventListener('click', clickHandler);
      };
    });
  }, []);

  const renderedText = Array.isArray(richText)
    ? richText.map((item) =>
        typeof item === 'string' ? renderTextWithFootnotesReferencesV2(item) : item,
      )
    : renderTextWithFootnotesReferencesV2(richText as string);

  return (
    <TextWrapper tag="span">
      <UeElement type="text" property="text" label="Copy">
        {(ueProps) => (
          <StyledText {...ueProps} ref={textRef}>
            {renderedText}
          </StyledText>
        )}
      </UeElement>
    </TextWrapper>
  );
});
